import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { fetchParametersJson, fetchParametersDraftJson, saveParameters, saveDraftParameters } from '@/services/logicAppsApi';
import { useDraftModeStore } from '@/stores/draftModeStore';
import { type ParametersFile } from '@/types/logicApps';

export const useParametersJson = (info: { projectName: string; appName: string; appId: string } | null) => {
  const { getOttoToken, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['parameters', info?.projectName?.toLowerCase(), info?.appName?.toLowerCase()],
    queryFn: async () => {
      if (!info?.projectName || !info?.appName) {
        return {};
      }

      return fetchParametersJson({
        projectName: info.projectName,
        appName: info.appName,
        getToken: getOttoToken,
      });
    },
    enabled: isAuthenticated && !!info?.projectName && !!info?.appName,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useParametersDraftJson = (info: { projectName: string; appName: string; appId: string } | null) => {
  const { getOttoToken, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['parameters-draft', info?.projectName?.toLowerCase(), info?.appName?.toLowerCase()],
    queryFn: async () => {
      if (!info?.projectName || !info?.appName) {
        return {};
      }

      return fetchParametersDraftJson({
        projectName: info.projectName,
        appName: info.appName,
        getToken: getOttoToken,
      });
    },
    enabled: isAuthenticated && !!info?.projectName && !!info?.appName,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Mutation hook to persist the full parameters.json contents for an app.
 * Invalidates the matching `useParameters` cache entry on success so the
 * editor and any downstream views observe the new values immediately.
 */
export const useSaveParameters = (
  info: { projectName: string; appName: string; appId: string } | null,
) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (parameters: ParametersFile) => {
      if (!info?.appName || !info?.projectName) {
        throw new Error('Project and app name are required to save parameters');
      }

      return saveParameters({
        projectName: info.projectName,
        appName: info.appName,
        getToken: getOttoToken,
        parameters,
      });
    },
    onSuccess: (_data, parameters) => {
      void queryClient.invalidateQueries({ queryKey: ['parameters', info?.projectName?.toLowerCase(), info?.appName?.toLowerCase()] });
      // Mirror into the draft store so publish + draft-equality checks see
      // the latest values without waiting for a query refetch.
      useDraftModeStore.getState().setProdArtifacts({ parameters: parameters as Record<string, unknown> });
    },
  });
};

/**
 * Mutation hook to persist the full parameters-draft.json contents for an app.
 * Invalidates the matching draft cache entry on success so the editor and
 * any downstream views observe the new values immediately.
 */
export const useSaveDraftParameters = (
  info: { projectName: string; appName: string; appId: string } | null,
) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (parameters: ParametersFile) => {
      if (!info?.appName || !info?.projectName) {
        throw new Error('Project and app name are required to save parameters');
      }

      return saveDraftParameters({
        projectName: info.projectName,
        appName: info.appName,
        getToken: getOttoToken,
        parameters,
      });
    },
    onSuccess: (_data, parameters) => {
      void queryClient.invalidateQueries({ queryKey: ['parameters-draft', info?.projectName?.toLowerCase(), info?.appName?.toLowerCase()] });
      // Mirror into the draft store so usePublishDraft forwards these to
      // publishDraft (which writes parameters.json). Without this, edits made
      // in the standalone Parameters editor never reach the published file.
      useDraftModeStore.getState().setDraftArtifacts({ parameters: parameters as Record<string, unknown> });
    },
  });
};
